import { useTranslation } from 'react-i18next'

import { Box, Divider, Stack, SxProps, Theme, Typography } from '@mui/material'

import responsiveHook from '@hooks/useResponsive'

import { BulletPoints } from '@pages/LandingPage/Features/components/BulletPoints'

import { TLGTypography } from '@components/TLGTypography'

import { scaleUpTransition } from '@utils/styles'

import {
  affordableFlatRateSubscriptionForEffectiveLeadGenerationLeftSideContentSx,
  affordableFlatRateSubscriptionForEffectiveLeadGenerationLeftSideWrapperSx,
  affordableFlatRateSubscriptionForEffectiveLeadGenerationRightSideImageSx,
  affordableFlatRateSubscriptionForEffectiveLeadGenerationRightSideWrapperSx,
  containerSx,
  contentGridSx,
  dividerWrapperSx,
  stayAheadOfTheCurveWithContinuousInnovationBulletPointsSx,
  stayAheadOfTheCurveWithContinuousInnovationLeftSideImageSx,
  stayAheadOfTheCurveWithContinuousInnovationLeftSideWrapperSx,
  stayAheadOfTheCurveWithContinuousInnovationRightSideContentSx,
  stayAheadOfTheCurveWithContinuousInnovationRightSideWrapperSx,
  titleSx,
} from './styles'

const Content2Section = () => {
  const { t } = useTranslation()
  const { isDesktop, ifDesktop } = responsiveHook.useResponsive()

  return (
    <Box data-testid="tech-vendors-features-content-2-section" sx={containerSx}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={contentGridSx}
      >
        <Box
          gridColumn={{
            xs: 'span 12',
            lg: 'span 6',
          }}
          sx={
            affordableFlatRateSubscriptionForEffectiveLeadGenerationLeftSideWrapperSx
          }
        >
          <Stack
            sx={
              affordableFlatRateSubscriptionForEffectiveLeadGenerationLeftSideContentSx
            }
          >
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t(
                'Affordable, Flat-Rate Subscription for Effective Lead Generation'
              )}
            </TLGTypography>
            <Typography>
              {t('Unlock Powerful Results at a Fraction of the Cost.')}
            </Typography>

            <BulletPoints
              list={[
                t(
                  'Our affordable, flat-rate monthly subscription ensures that you have predictable and manageable costs for your lead generation efforts. '
                ),
                t(
                  "It's a fraction of the cost of traditional demand generation campaigns, allowing you to allocate your budget effectively and maximize your returns."
                ),
                t(
                  'Unlike pay-per-engagement models, we focus on delivering high-quality leads, allowing for more meaningful and mutually beneficial engagements.'
                ),
              ]}
            />
          </Stack>
        </Box>

        <Box
          gridColumn={{
            xs: 'span 12',
            lg: 'span 6',
          }}
          sx={
            affordableFlatRateSubscriptionForEffectiveLeadGenerationRightSideWrapperSx
          }
        >
          <Box
            alt={t<string>(
              'Tech vendor my solutions and solution view pages screenshot'
            )}
            loading="lazy"
            component="img"
            src="/images/tech-vendor-my-solutions-and-solution-view-pages.webp"
            sx={
              {
                ...affordableFlatRateSubscriptionForEffectiveLeadGenerationRightSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        {isDesktop && (
          <Box gridColumn="span 12" sx={dividerWrapperSx}>
            <Divider />
          </Box>
        )}

        <Box
          gridColumn={{
            xs: 'span 12',
            lg: 'span 6',
          }}
          sx={stayAheadOfTheCurveWithContinuousInnovationLeftSideWrapperSx}
        >
          <Box
            alt={t<string>('Tech vendor settings page screenshot')}
            loading="lazy"
            component="img"
            src="/images/tech-vendor-settings.webp"
            sx={
              {
                ...stayAheadOfTheCurveWithContinuousInnovationLeftSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        <Box
          gridColumn={{
            xs: 'span 12',
            lg: 'span 6',
          }}
          sx={stayAheadOfTheCurveWithContinuousInnovationRightSideWrapperSx}
        >
          <Stack
            sx={stayAheadOfTheCurveWithContinuousInnovationRightSideContentSx}
          >
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('Stay Ahead of the Curve with Continuous Innovation')}
            </TLGTypography>
            <Typography>
              {t('Embrace the Future of Tech Marketing & Sales.')}
            </Typography>

            <BulletPoints
              list={[
                t(
                  'Join a marketplace that is constantly evolving and innovating to meet the dynamic needs of Tech Customers and the technology industry and enjoy an optimal matchmaking experience.'
                ),
                t(
                  'Gain access to the latest tools and features that keep you at the forefront of the industry.'
                ),
                t(
                  'Position yourself as an innovative and forward-thinking technology partner, attracting like-minded Tech Customers who are seeking cutting-edge solutions.'
                ),
              ]}
              containerSx={
                stayAheadOfTheCurveWithContinuousInnovationBulletPointsSx
              }
            />
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}

export { Content2Section }
